import {
  BugAntIcon,
  ChevronRightIcon,
  CpuChipIcon,
  SparklesIcon,
  TrophyIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';
import React, { useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router';

import { LeftNav, type NavItem as BaseNavItem } from '@/components/chariot-ui';

type NavItem = BaseNavItem & { path: string };

const mainNavItems: NavItem[] = [
  {
    icon: <UsersIcon className="size-5" />,
    label: 'Customers',
    path: 'customers',
  },
  {
    icon: <TrophyIcon className="size-5" />,
    label: 'Leaderboard',
    path: 'leaderboard',
  },
  {
    icon: <BugAntIcon className="size-5" />,
    label: 'Bug Bounty',
    path: 'bug-bounty',
  },
  {
    icon: <SparklesIcon className="size-5" />,
    label: '0 Days',
    path: 'zero-days',
  },
  {
    icon: <CpuChipIcon className="size-5" />,
    label: 'Operations',
    path: 'master',
  },
];

export function CustomerManagementNavigation() {
  const location = useLocation();
  const [, setSearchParams] = useSearchParams();
  const [collapsed, setCollapsed] = useState(true);

  return (
    <div className="border-chariot-stroke relative z-20 border-r">
      <button
        aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
        className={
          'border-chariot-stroke bg-chariot-background-primary absolute top-4 right-[-27px] z-30 flex h-10 w-7 items-center justify-center rounded-r-full border border-l-0 shadow transition-all duration-200'
        }
        onClick={() => setCollapsed(c => !c)}
        tabIndex={0}
        style={{ padding: 0 }}
      >
        <span
          className={
            'text-chariot-stroke transition-transform duration-300' +
            (collapsed ? ' rotate-0' : ' -rotate-180')
          }
        >
          <ChevronRightIcon className="size-5" />
        </span>
      </button>
      <LeftNav
        collapsed={collapsed}
        brandLogo={
          <img
            src="/icons/praetorian-icon.svg"
            alt="Praetorian"
            className="size-full"
          />
        }
        mainNavItems={getItems(mainNavItems, location, setSearchParams)}
      />
    </div>
  );
}

const VIEW_BASED_PATHS = new Set([
  'zero-days',
  'bug-bounty',
  'leaderboard',
  'master',
]);

const getItems = (
  navItems: NavItem[],
  location: ReturnType<typeof useLocation>,
  setSearchParams: ReturnType<typeof useSearchParams>[1]
) => {
  const currentView = new URLSearchParams(location.search).get('view');

  return navItems.map(item => {
    // View-based items are selected when their ?view= param is in the URL
    // The 'customers' item is selected when no view is active (main dashboard)
    const isSelected = VIEW_BASED_PATHS.has(item.path)
      ? currentView === item.path
      : item.path === 'customers'
        ? !currentView
        : location.pathname.split('/')[2] === item.path;

    return {
      ...item,
      linkTo: (children: React.ReactNode) => {
        if (VIEW_BASED_PATHS.has(item.path)) {
          return (
            <button
              type="button"
              onClick={() =>
                setSearchParams(
                  prev => {
                    const p = new URLSearchParams(prev);
                    p.delete('tab');
                    p.set('view', item.path);
                    return p;
                  },
                  { replace: true }
                )
              }
              className="w-full text-left"
            >
              {children}
            </button>
          );
        }
        if (item.path === 'customers') {
          return (
            <Link
              to={{ search: '?tab=global-customer-dashboard' }}
              relative="path"
              replace
            >
              {children}
            </Link>
          );
        }
        // Placeholder: other nav items don't route anywhere yet.
        return <div>{children}</div>;
      },
      selected: isSelected,
    };
  });
};

CustomerManagementNavigation.displayName = 'CustomerManagementNavigation';
